import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  Divider,
  Paper,
  Fade,
  Chip,
  useTheme,
  alpha,
  TextField,
} from "@mui/material";
import {
  Add,
  FilterList,
  Delete,
  Fullscreen,
  Info,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { JustInfo } from "./Icon";
import { ViewModeMenu } from "./ViewMode";
import { CameraExportButton } from "../../utils/export/componentExporter";

const Toolbar = ({
  title,
  titleInfo,
  subtitle,
  showSelectedCount = false,
  selectedCount = 0,
  totalCount = 0,
  onRemove,
  onAdd,
  viewMode,
  viewOptions,
  onToggleView,
  filterActive = false,
  onToggleFilter,
  fullscreenOpen = false,
  onToggleFullscreen,
  children,
  showActionBar = false,
  variant = "elevated",
  size = "medium",
  additionalControls,
  renderAIAssistant,
  allowTitleEditing,
  updateTitle,
  handleExportAsImage,
  exportRef,
  canEdit = true,
}) => {
  const theme = useTheme();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState("");

  useEffect(() => {
    setTempTitle(title || "");
  }, [title]);

  const sizeStyles = useMemo(
    () => ({
      small: {
        p: 1.5,
        titleVariant: "subtitle1",
        titleWeight: 700,
        titleSize: "1rem",
      },
      medium: {
        p: 3,
        titleVariant: "h6",
        titleWeight: 800,
        titleSize: "1.25rem",
      },
      large: {
        p: 4,
        titleVariant: "h5",
        titleWeight: 800,
        titleSize: "1.5rem",
      },
    }),
    []
  );

  const currentSize = sizeStyles[size] || sizeStyles.medium;

  const toolbarStyles = useMemo(() => {
    const baseStyles = {
      p: currentSize.p,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      transition:
        "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
    };

    const variantMap = {
      flat: {
        bgcolor: "transparent",
        borderColor: "transparent",
        boxShadow: "none",
      },
      elevated: {
        bgcolor: alpha(theme.palette.background.paper, 0.98),
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      },
      outlined: {
        bgcolor: "background.paper",
        borderColor: alpha(theme.palette.primary.main, 0.35),
        boxShadow: "none",
      },
    };

    return {
      ...baseStyles,
      ...(variantMap[variant] || variantMap.elevated),
    };
  }, [currentSize.p, theme, variant]);

  const hasPrimaryActions =
    Boolean(onToggleFilter) ||
    Boolean(renderAIAssistant) ||
    Boolean(canEdit && (onAdd || onRemove));
  const hasToolActions =
    Boolean(onToggleView) ||
    Boolean(handleExportAsImage && exportRef) ||
    Boolean(!fullscreenOpen && onToggleFullscreen);

  const handleTitleEdit = () => {
    if (isEditingTitle) {
      updateTitle?.(tempTitle);
      setIsEditingTitle(false);
      return;
    }

    setIsEditingTitle(true);
  };

  const handleCancelEdit = () => {
    setTempTitle(title || "");
    setIsEditingTitle(false);
  };

  const renderTitle = () => {
    if (!title && !subtitle) return null;

    return (
      <Stack spacing={0.75} sx={{ minWidth: 0 }}>
        {title && (
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ minWidth: 0 }}
          >
            {isEditingTitle ? (
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ minWidth: 0, width: "100%", maxWidth: 440 }}
              >
                <TextField
                  label='Schedule Title'
                  fullWidth
                  value={tempTitle}
                  onChange={(event) => setTempTitle(event.target.value)}
                  size='small'
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                    },
                  }}
                />
                <Tooltip title='Save'>
                  <IconButton
                    onClick={handleTitleEdit}
                    color='primary'
                    size='small'
                  >
                    <SaveIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Cancel'>
                  <IconButton
                    onClick={handleCancelEdit}
                    color='error'
                    size='small'
                  >
                    <CancelIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <>
                <Typography
                  variant={currentSize.titleVariant}
                  sx={{
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: currentSize.titleSize,
                    fontWeight: currentSize.titleWeight,
                    lineHeight: 1.25,
                    letterSpacing: 0,
                  }}
                >
                  {title}
                </Typography>
                {allowTitleEditing && canEdit && (
                  <Tooltip title='Edit schedule title'>
                    <IconButton
                      onClick={handleTitleEdit}
                      size='small'
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {titleInfo && (
                  <Box sx={{ flexShrink: 0 }}>
                    <JustInfo title={titleInfo} />
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}

        {(subtitle || showSelectedCount || totalCount > 0) && (
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            flexWrap='wrap'
            useFlexGap
            sx={{ minWidth: 0 }}
          >
            {subtitle && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.4,
                  letterSpacing: 0,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    );
  };

  const renderAdditionalControls = () => {
    if (!additionalControls) return null;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        {Array.isArray(additionalControls)
          ? additionalControls.map((control, index) => (
              <Box key={index}>{control}</Box>
            ))
          : additionalControls}
      </Box>
    );
  };

  const renderActionButtons = () => (
    <Stack
      direction='row'
      spacing={0.75}
      alignItems='center'
      justifyContent={{ xs: "flex-start", sm: "flex-end" }}
      flexWrap='wrap'
      useFlexGap
    >
      {onToggleFilter && (
        <Tooltip title={`${filterActive ? "Hide" : "Show"} filters`}>
          <IconButton
            size='small'
            color={filterActive ? "primary" : "default"}
            onClick={onToggleFilter}
            sx={{
              bgcolor: filterActive
                ? alpha(theme.palette.primary.main, 0.1)
                : "transparent",
              "&:hover": {
                bgcolor: filterActive
                  ? alpha(theme.palette.primary.main, 0.16)
                  : "action.hover",
              },
            }}
          >
            <FilterList fontSize='small' />
          </IconButton>
        </Tooltip>
      )}

      {canEdit && onAdd && (
        <Tooltip title='Add new item'>
          <IconButton
            size='small'
            color='primary'
            onClick={onAdd}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.18),
              },
            }}
          >
            <Add fontSize='small' />
          </IconButton>
        </Tooltip>
      )}

      {canEdit && onRemove && (
        <Tooltip
          title={`Delete ${selectedCount} selected item${selectedCount === 1 ? "" : "s"}`}
        >
          <span>
            <IconButton
              size='small'
              color='error'
              onClick={onRemove}
              disabled={selectedCount <= 0}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.18),
                },
              }}
            >
              <Badge badgeContent={selectedCount} color='error' max={99}>
                <Delete fontSize='small' />
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
      )}

      {renderAIAssistant}

      {hasPrimaryActions && (additionalControls || hasToolActions) && (
        <Divider orientation='vertical' flexItem sx={{ mx: 0.25 }} />
      )}

      {renderAdditionalControls()}

      {additionalControls && hasToolActions && (
        <Divider orientation='vertical' flexItem sx={{ mx: 0.25 }} />
      )}

      {onToggleView && (
        <ViewModeMenu
          options={viewOptions}
          value={viewMode}
          onChange={onToggleView}
        />
      )}

      {handleExportAsImage && exportRef && (
        <CameraExportButton
          onClick={handleExportAsImage}
          exportRef={exportRef}
          tooltipTitle='Export as image'
          sx={{ ml: 0.25 }}
        />
      )}

      {!fullscreenOpen && onToggleFullscreen && (
        <Tooltip title='Enter fullscreen'>
          <IconButton
            size='small'
            onClick={onToggleFullscreen}
            sx={{
              color: alpha(theme.palette.primary.main, 0.85),
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.35),
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderColor: alpha(theme.palette.primary.main, 0.55),
              },
            }}
          >
            <Fullscreen fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Paper elevation={0} sx={toolbarStyles}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent='space-between'
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{renderTitle()}</Box>
          {renderActionButtons()}
        </Stack>
      </Paper>

      {showActionBar && selectedCount > 0 && (
        <Fade in>
          <Paper
            elevation={2}
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.18),
              borderRadius: 2,
            }}
          >
            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              flexWrap='wrap'
              useFlexGap
            >
              <Stack direction='row' alignItems='center' spacing={1}>
                <Info fontSize='small' color='primary' />
                <Typography variant='body2' fontWeight={700}>
                  {selectedCount} item{selectedCount > 1 ? "s" : ""} selected
                </Typography>
              </Stack>
              <Box sx={{ flexGrow: 1 }} />
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                {children}
              </Stack>
            </Stack>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default Toolbar;
